import React from 'react'
import AdminNavbar from "../Navbars/AdminNavbar.js";
import AdminSidebar from '../Sidebar/AdminSidebar';
import { UserApis } from '../../apis/userApi';
import { Oval } from 'react-loader-spinner'
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { SuperAdminApis } from '../../apis/superAdminApi';

function Tracking() {
    const [trackingDetails, setTrackingDetails] = React.useState<any>('Empty');
    const [trackingId, setTrackingId] = React.useState<any>('');
    const [isMultiple, setIsMultiple] = React.useState<boolean>(false);
    const [loader, setLoader] = React.useState<boolean>(false);
    const [percentage, setPercentage] = React.useState<any>(0);
    const [expandedRow, setExpandedRow] = React.useState<number | null>(null);

    const exportTrackingToExcel = (trackingData: any[], manifestInfo: any = {}) => {
        if (!trackingData?.length) { toast.error('No tracking data to export'); return; }

        const exportData = trackingData.map((d: any, i: number) => {
            const trackingNum = d.TrackingNumber || '';
            const mInfo = manifestInfo[trackingNum] || {};
            return {
                'S/N': i + 1,
                'Tracking Number': trackingNum || 'N/A',
                'Airwaybill': mInfo.airwaybill_number || 'N/A',
                'Destination Branch': mInfo.destination_branch || 'N/A',
                'Fleet': mInfo.fleet || 'N/A',
                'Sender': d.ShipmentInfo?.SenderName || 'N/A',
                'Receiver': d.ShipmentInfo?.ReceiverName || 'N/A',
                'Receiver Phone': d.ShipmentInfo?.ReceiverPhone || 'N/A',
                'Receiver Address': d.ShipmentInfo?.ReceiverAddress || 'N/A',
                'Weight': d.ShipmentInfo?.Weight
                    ? `${d.ShipmentInfo.Weight} ${d.ShipmentInfo.WeightMeasurement || 'KG'}` : 'N/A',
                'No. of Items': d.ShipmentInfo?.NoOfItems ?? 'N/A',
                'Description': d.ShipmentInfo?.GoodsDescription || 'N/A',
                // Money columns are left BLANK (not 'N/A') when there is no payment
                // yet, so Excel keeps them numeric and SUM/AVERAGE still work.
                'Custom Value': d.ShipmentInfo?.CustomValue != null ? Number(d.ShipmentInfo.CustomValue) : '',
                'Custom Currency': d.ShipmentInfo?.CustomCurrency || '',
                'Delivery Cost (Paid)': d.ShipmentInfo?.DeliveryCost != null ? Number(d.ShipmentInfo.DeliveryCost) : '',
                'Delivery Rate': d.ShipmentInfo?.DeliveryRate != null ? Number(d.ShipmentInfo.DeliveryRate) : '',
                'Fine Amount': d.ShipmentInfo?.FineAmount != null ? Number(d.ShipmentInfo.FineAmount) : '',
                'Fine Days': d.ShipmentInfo?.FineDays != null ? Number(d.ShipmentInfo.FineDays) : '',
                'Delivered By (Rider)': d.ShipmentInfo?.PaidRiderName || '',
                'Paid To (Wallet Owner)': d.ShipmentInfo?.WalletName || '',
                'Wallet Email': d.ShipmentInfo?.WalletEmail || '',
                'Last Event': d.TrackingEventName || 'N/A',
                'Notes': d.TrackingNotes || 'N/A',
                'Last Location': d.TrackingLocation || 'N/A',
                'Last Date': d.TrackingDate || 'N/A',
                'Export Date': new Date().toLocaleString(),
            };
        });

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(exportData);
        // One width per column, in the same order as the object above.
        ws['!cols'] = [
            { wch: 5 },   // S/N
            { wch: 18 },  // Tracking Number
            { wch: 18 },  // Airwaybill
            { wch: 18 },  // Destination Branch
            { wch: 22 },  // Fleet
            { wch: 20 },  // Sender
            { wch: 20 },  // Receiver
            { wch: 16 },  // Receiver Phone
            { wch: 35 },  // Receiver Address
            { wch: 12 },  // Weight
            { wch: 10 },  // No. of Items
            { wch: 30 },  // Description
            { wch: 14 },  // Custom Value
            { wch: 12 },  // Custom Currency
            { wch: 18 },  // Delivery Cost (Paid)
            { wch: 14 },  // Delivery Rate
            { wch: 12 },  // Fine Amount
            { wch: 10 },  // Fine Days
            { wch: 22 },  // Delivered By (Rider)
            { wch: 24 },  // Paid To (Wallet Owner)
            { wch: 30 },  // Wallet Email
            { wch: 20 },  // Last Event
            { wch: 22 },  // Notes
            { wch: 20 },  // Last Location
            { wch: 20 },  // Last Date
            { wch: 20 },  // Export Date
        ];
        XLSX.utils.book_append_sheet(wb, ws, 'Tracking Results');
        XLSX.writeFile(wb, `tracking_results_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success(`Exported ${exportData.length} records successfully!`);
    };

    const handleTrackingExport = async () => {
        if (!trackingDetails?.length) { toast.error('No tracking data to export'); return; }
        try {
            toast.info('Preparing export...');
            const shipmentNumbers = trackingDetails.map((d: any) => d.TrackingNumber).filter(Boolean);
            let manifestInfo = {};
            if (shipmentNumbers.length > 0) {
                try {
                    const res = await SuperAdminApis.getShipmentManifestInfo(shipmentNumbers);
                    if (res?.data?.status) manifestInfo = res.data.data;
                } catch (e) { console.log('Manifest info fetch failed, exporting without it'); }
            }
            exportTrackingToExcel(trackingDetails, manifestInfo);
        } catch (e) { toast.error('Export failed'); console.error(e); }
    };

    const track = React.useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoader(true);
        setExpandedRow(null);
        let count = 1;

        const barcodeValues = Array.from(new Set<string>(trackingId.split(',').map((id: string) => id.trim()).filter(Boolean)));
        const batches: string[][] = [];
        const aggregated: any[] = [];

        for (let i = 0; i < barcodeValues.length; i += 5) batches.push(barcodeValues.slice(i, i + 5));

        try {
            for (const batch of batches) {
                const formData = new FormData();
                formData.append('reference', batch.join(','));
                const response: any = await UserApis.trackShipment(formData);
                if (!response.data.status) {
                    toast.error('Err: Network error, retry.');
                    setLoader(false);
                    return;
                }
                count += 1;
                aggregated.push(response?.data?.data);
                setPercentage((((count - 1) / batches.length) * 100).toFixed(1));
            }
            const flat = aggregated.flat();
            setTrackingDetails(flat);
            setIsMultiple(flat.length > 1);
            setPercentage(0);
        } catch (err) {
            toast.error('Tracking failed');
        } finally {
            setLoader(false);
        }
    }, [trackingId]);

    // Money formatter for the delivery-payment fields. Returns null (not "₦0.00")
    // for missing values so DetailItem falls through to its em-dash placeholder.
    const fmtNaira = (amount: any) =>
        amount == null || amount === ''
            ? null
            : '₦' + Number(amount).toLocaleString('en-NG', { minimumFractionDigits: 2 });

    // Reusable detail-row component
    const DetailItem = ({ label, value }: { label: string; value: any }) => (
        <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">{label}</p>
            <p className="text-sm text-gray-900 mt-0.5 break-words">{value || <span className="text-gray-400">—</span>}</p>
        </div>
    );

    // Shipment info card (used in single-track view)
    const ShipmentInfoCard = ({ info }: { info: any }) => {
        if (!info) return null;
        const manifest = info.ManifestInfo;
        return (
            <div className="mb-5 border border-gray-200 rounded-[10px] bg-gradient-to-br from-red-50/40 to-white p-5">
                <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-full bg-[#E04343]/10 flex items-center justify-center">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M20 7L12 3L4 7M20 7L12 11M20 7V17L12 21M12 11L4 7M12 11V21M4 7V17L12 21"
                                stroke="#E04343" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                    <div>
                        <h4 className="text-sm font-semibold text-gray-900">Shipment Details</h4>
                        <p className="text-xs text-gray-500">{info.ShipmentNumber}</p>
                    </div>
                    {info.CurrentStatus && (
                        <span className="ml-auto text-xs font-medium px-2.5 py-1 rounded-full bg-[#E04343]/10 text-[#E04343]">
                            {info.CurrentStatus}
                        </span>
                    )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <DetailItem label="Sender" value={info.SenderName} />
                    <DetailItem label="Receiver" value={info.ReceiverName} />
                    <DetailItem label="Receiver Phone" value={info.ReceiverPhone} />
                    <DetailItem
                        label="Weight"
                        value={info.Weight ? `${info.Weight} ${info.WeightMeasurement || 'KG'}` : null}
                    />
                    <DetailItem label="No. of Items" value={info.NoOfItems} />
                    <DetailItem label="Description" value={info.GoodsDescription} />
                    <DetailItem
                        label="Custom Value"
                        value={info.CustomValue != null && info.CustomValue !== ''
                            ? `${info.CustomCurrency || ''} ${Number(info.CustomValue).toLocaleString()}`.trim()
                            : null}
                    />
                    <div className="col-span-2 md:col-span-3">
                        <DetailItem label="Receiver Address" value={info.ReceiverAddress} />
                    </div>
                </div>

                {/* Delivery payment — only exists once the shipment reaches POD, so
                    the whole strip is hidden until then rather than showing zeroes.
                    "Paid To" is the wallet that was CREDITED, which for a rider under
                    a fleet is the fleet, not the rider — so the delivering rider is
                    shown separately. */}
                {info.DeliveryCost != null && (
                    <div className="mt-4 pt-3 border-t border-gray-200">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-2">
                            Delivery Payment
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <DetailItem label="Delivery Cost (Paid)" value={fmtNaira(info.DeliveryCost)} />
                            {info.DeliveryRate != null && Number(info.DeliveryRate) !== Number(info.DeliveryCost) && (
                                <DetailItem label="Delivery Rate" value={fmtNaira(info.DeliveryRate)} />
                            )}
                            {Number(info.FineAmount) > 0 && (
                                <DetailItem
                                    label="Fine Deducted"
                                    value={
                                        <span className="text-[#E04343] font-semibold">
                                            {fmtNaira(info.FineAmount)}
                                            {Number(info.FineDays) > 0 && (
                                                <span className="text-gray-400 font-normal">
                                                    {' '}({info.FineDays} day{Number(info.FineDays) === 1 ? '' : 's'} late)
                                                </span>
                                            )}
                                        </span>
                                    }
                                />
                            )}
                            <DetailItem label="Delivered By" value={info.PaidRiderName} />
                            <DetailItem label="Paid To (Wallet)" value={info.WalletName} />
                            <DetailItem label="Wallet Email" value={info.WalletEmail} />
                        </div>
                    </div>
                )}

                {/* Manifest info — compact strip below shipment details */}
                {manifest && (
                    <div className="mt-4 pt-3 border-t border-gray-200">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-2">Manifest</p>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-700">
                            <span><span className="text-gray-400">Name: </span>{manifest.ManifestName || '—'}</span>
                            {manifest.AirwaybillNumber && (
                                <span><span className="text-gray-400">AWB: </span>{manifest.AirwaybillNumber}</span>
                            )}
                            <span>
                                <span className="text-gray-400">Route: </span>
                                {manifest.SenderBranch || '—'} → {manifest.DestinationBranch || '—'}
                            </span>
                            {manifest.Deadline && (
                                <span><span className="text-gray-400">Deadline: </span>{manifest.Deadline}</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Runway manifest (Run) info */}
                {info.RunwayManifestInfo && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-2">Run Manifest</p>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-700">
                            <span><span className="text-gray-400">Run ID: </span>{info.RunwayManifestInfo.RunId || '—'}</span>
                            {info.RunwayManifestInfo.RunName && (
                                <span><span className="text-gray-400">Name: </span>{info.RunwayManifestInfo.RunName}</span>
                            )}
                            {info.RunwayManifestInfo.FleetName && (
                                <span><span className="text-gray-400">Fleet: </span>{info.RunwayManifestInfo.FleetName}</span>
                            )}
                            {info.RunwayManifestInfo.RiderName && (
                                <span><span className="text-gray-400">Rider: </span>{info.RunwayManifestInfo.RiderName}</span>
                            )}
                            {info.RunwayManifestInfo.Branch && (
                                <span><span className="text-gray-400">Branch: </span>{info.RunwayManifestInfo.Branch}</span>
                            )}
                            {info.RunwayManifestInfo.Notes && (
                                <span className="text-[#E04343]">{info.RunwayManifestInfo.Notes}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Inline expanded detail for multi-track rows
    const ExpandedRowDetails = ({ info }: { info: any }) => {
        if (!info) {
            return (
                <div className="px-4 py-3 bg-gray-50 text-sm text-gray-500 italic">
                    No local shipment record found for this tracking number.
                </div>
            );
        }
        const manifest = info.ManifestInfo;
        const run = info.RunwayManifestInfo;
        return (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <DetailItem label="Sender" value={info.SenderName} />
                    <DetailItem label="Receiver" value={info.ReceiverName} />
                    <DetailItem label="Receiver Phone" value={info.ReceiverPhone} />
                    <DetailItem label="Weight" value={info.Weight ? `${info.Weight} ${info.WeightMeasurement || 'KG'}` : null} />
                    <DetailItem label="No. of Items" value={info.NoOfItems} />
                    <DetailItem label="Description" value={info.GoodsDescription} />
                    <DetailItem
                        label="Custom Value"
                        value={info.CustomValue != null && info.CustomValue !== ''
                            ? `${info.CustomCurrency || ''} ${Number(info.CustomValue).toLocaleString()}`.trim()
                            : null}
                    />
                    <div className="col-span-2">
                        <DetailItem label="Receiver Address" value={info.ReceiverAddress} />
                    </div>
                </div>

                {/* Delivery payment — hidden until POD creates one. Mirrors the
                    single-track card so both views show the same figures. */}
                {info.DeliveryCost != null && (
                    <div className="mt-4 pt-3 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <DetailItem label="Delivery Cost (Paid)" value={fmtNaira(info.DeliveryCost)} />
                        {Number(info.FineAmount) > 0 && (
                            <DetailItem
                                label="Fine Deducted"
                                value={<span className="text-[#E04343] font-semibold">{fmtNaira(info.FineAmount)}</span>}
                            />
                        )}
                        <DetailItem label="Delivered By" value={info.PaidRiderName} />
                        <DetailItem label="Paid To (Wallet)" value={info.WalletName} />
                        <DetailItem label="Wallet Email" value={info.WalletEmail} />
                    </div>
                )}
                {manifest && (
                    <div className="mt-4 pt-3 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <DetailItem label="Manifest" value={manifest.ManifestName} />
                        <DetailItem label="AWB" value={manifest.AirwaybillNumber} />
                        <DetailItem label="Route" value={manifest.SenderBranch && manifest.DestinationBranch ? `${manifest.SenderBranch} → ${manifest.DestinationBranch}` : null} />
                    </div>
                )}
                {run && (
                    <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <DetailItem label="Run ID" value={run.RunId} />
                        <DetailItem label="Fleet" value={run.FleetName} />
                        <DetailItem label="Rider" value={run.RiderName} />
                        <DetailItem label="Branch" value={run.Branch} />
                    </div>
                )}
            </div>
        );
    };

    const single = Array.isArray(trackingDetails) && trackingDetails.length === 1 ? trackingDetails[0] : null;

    return (
        <div>
            <AdminSidebar title="Tracking" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Tracking" />

                <div className="lg:px-6 pb-6 px-4 pt-5 bg-[#FAFAFA] min-h-screen">
                    <div className="grid grid-cols-1 gap-5">
                        {/* Tracking input */}
                        <form onSubmit={track}>
                            <div className="flex items-start mx-auto">
                                <div className="w-full lg:pr-4">
                                    <label className="block mb-2 text-sm font-semibold text-[#19213D]">Tracking</label>
                                    <textarea
                                        value={trackingId.split(',').join('\n')}
                                        onChange={(e) => setTrackingId(e.target.value.split('\n').join(','))}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        rows={6}
                                        placeholder="Enter one tracking ID per line"
                                    />
                                </div>
                                <button type="submit"
                                    className="inline-flex items-center py-2 px-4 ml-2 mt-7 text-sm font-medium text-white bg-[#E04343] rounded-[5px] hover:bg-[#c93838]">
                                    Track
                                </button>
                            </div>
                        </form>

                        {/* Results panel */}
                        <div className="bg-white rounded-[10px] px-[15px] py-[16px]">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-[14px] font-semibold">Route Details</h3>
                                {isMultiple && trackingDetails.length > 0 && (
                                    <button onClick={handleTrackingExport}
                                        className="bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-700 flex items-center gap-2">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"
                                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        Export {trackingDetails.length} Records
                                    </button>
                                )}
                            </div>

                            <div className="border border-[#E2E8F0] rounded-[15px] min-h-[60vh] max-h-[75vh] overflow-auto">
                                {loader ? (
                                    <div className="flex flex-col mt-[25vh] mx-5 px-5">
                                        <div className="w-full bg-gray-200 rounded-full">
                                            <div className="bg-[#E04343] text-xs font-medium text-white text-center p-0.5 leading-none rounded-full"
                                                style={{ width: `${percentage}%` }}>{percentage}%</div>
                                        </div>
                                        <div className="flex justify-center mt-4">
                                            <Oval visible height="60" width="60" color="#E04343" secondaryColor="#FCBF94" ariaLabel="oval-loading" />
                                        </div>
                                        <div className="flex justify-center mt-3"><span>Kindly wait!!!</span></div>
                                    </div>
                                ) : isMultiple ? (
                                    // ═══ MULTIPLE: expandable rows ═══
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-50 text-gray-700">
                                                <tr>
                                                    <th className="px-3 py-3 text-left font-semibold w-10"></th>
                                                    <th className="px-3 py-3 text-left font-semibold">S/N</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Tracking Number</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Receiver</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Current Status</th>
                                                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">Custom Value</th>
                                                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">Delivery Cost</th>
                                                    <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">Fine</th>
                                                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Paid To (Wallet)</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Last Event</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Notes</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Location</th>
                                                    <th className="px-3 py-3 text-left font-semibold">Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Array.isArray(trackingDetails) && trackingDetails.map((d: any, i: number) => {
                                                    const hasInfo = !!d.ShipmentInfo;
                                                    const isOpen = expandedRow === i;
                                                    return (
                                                        <React.Fragment key={i}>
                                                            <tr className={`border-t border-gray-200 hover:bg-gray-50 ${isOpen ? 'bg-gray-50' : ''}`}>
                                                                <td className="px-3 py-3">
                                                                    <button
                                                                        onClick={() => setExpandedRow(isOpen ? null : i)}
                                                                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-transform ${hasInfo ? 'bg-[#E04343]/10 text-[#E04343] hover:bg-[#E04343]/20' : 'bg-gray-100 text-gray-400'} ${isOpen ? 'rotate-90' : ''}`}
                                                                        title={hasInfo ? 'View shipment details' : 'No local record'}
                                                                    >
                                                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                                                            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                    </button>
                                                                </td>
                                                                <td className="px-3 py-3">{i + 1}</td>
                                                                <td className="px-3 py-3 font-medium text-gray-900">{d.TrackingNumber || '—'}</td>
                                                                <td className="px-3 py-3">{d.ShipmentInfo?.ReceiverName || <span className="text-gray-400">—</span>}</td>
                                                                <td className="px-3 py-3">
                                                                    {d.ShipmentInfo?.CurrentStatus
                                                                        ? <span className="bg-green-100 text-green-700 text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap">{d.ShipmentInfo.CurrentStatus}</span>
                                                                        : <span className="text-gray-400">—</span>}
                                                                </td>
                                                                <td className="px-3 py-3 text-right whitespace-nowrap">
                                                                    {d.ShipmentInfo?.CustomValue != null
                                                                        ? `${d.ShipmentInfo.CustomCurrency || ''} ${Number(d.ShipmentInfo.CustomValue).toLocaleString()}`.trim()
                                                                        : <span className="text-gray-400">—</span>}
                                                                </td>
                                                                <td className="px-3 py-3 text-right whitespace-nowrap">
                                                                    {fmtNaira(d.ShipmentInfo?.DeliveryCost) || <span className="text-gray-400">—</span>}
                                                                </td>
                                                                <td className="px-3 py-3 text-right whitespace-nowrap">
                                                                    {Number(d.ShipmentInfo?.FineAmount) > 0
                                                                        ? <span className="text-[#E04343] font-semibold">{fmtNaira(d.ShipmentInfo.FineAmount)}</span>
                                                                        : <span className="text-gray-400">—</span>}
                                                                </td>
                                                                <td className="px-3 py-3">
                                                                    {d.ShipmentInfo?.WalletName || d.ShipmentInfo?.WalletEmail
                                                                        ? (
                                                                            <div className="leading-tight">
                                                                                <div className="text-gray-900">{d.ShipmentInfo.WalletName || '—'}</div>
                                                                                {d.ShipmentInfo.WalletEmail && (
                                                                                    <div className="text-[11px] text-gray-500 break-all">{d.ShipmentInfo.WalletEmail}</div>
                                                                                )}
                                                                            </div>
                                                                        )
                                                                        : <span className="text-gray-400">—</span>}
                                                                </td>
                                                                <td className="px-3 py-3">{d.TrackingEventName || '—'}</td>
                                                                <td className="px-3 py-3 text-[#E04343]">{d.TrackingNotes || '—'}</td>
                                                                <td className="px-3 py-3">{d.TrackingLocation || '—'}</td>
                                                                <td className="px-3 py-3 text-gray-600">{d.TrackingDate || '—'}</td>
                                                            </tr>
                                                            {isOpen && (
                                                                <tr>
                                                                    {/* Must match the header count (13) or the expanded
                                                                        panel stops short of the table width. */}
                                                                    <td colSpan={13} className="p-0">
                                                                        <ExpandedRowDetails info={d.ShipmentInfo} />
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : single ? (
                                    // ═══ SINGLE: info card + timeline ═══
                                    <div className="px-5 py-5">
                                        <ShipmentInfoCard info={single.ShipmentInfo} />

                                        {single.TrackingDetail && single.TrackingDetail.length > 0 ? (
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-900 mb-4 px-1">Tracking History</h4>
                                                <ol className="relative border-l-2 border-dotted border-gray-300 ml-3">
                                                    {single.TrackingDetail.slice(0).reverse().map((datas: any, index: number) => (
                                                        datas?.TrackingError === '|6|No record to download.' ? (
                                                            <div key={index} className="flex justify-center">
                                                                <p className="mb-4 text-sm font-normal text-gray-400 max-w-[300px]">No Record Found!</p>
                                                            </div>
                                                        ) : (
                                                            <li key={index} className="ml-6 mb-5">
                                                                <div className="absolute w-[20px] h-[20px] bg-[#E04343] rounded-full -left-[11px] border-2 border-white shadow"></div>
                                                                <time className="text-sm font-semibold text-gray-900 block">{datas?.TrackingLocation}</time>
                                                                <p className="mt-1 text-sm text-gray-600">{datas?.TrackingEventName}</p>
                                                                {datas?.TrackingNotes && (
                                                                    <p className="mt-0.5 text-xs text-[#E04343]">{datas.TrackingNotes}</p>
                                                                )}
                                                                <p className="text-xs text-gray-400 mt-1">
                                                                    <span className="font-medium text-gray-500">Event: </span>{datas?.TrackingDate}
                                                                </p>
                                                                {datas?.TrackingUTCDate && (
                                                                    <p className="text-xs text-gray-400">
                                                                        <span className="font-medium text-gray-500">Received (UTC): </span>{datas.TrackingUTCDate}
                                                                    </p>
                                                                )}
                                                            </li>
                                                        )
                                                    ))}
                                                </ol>
                                            </div>
                                        ) : (
                                            <div className="text-center py-10 text-gray-500 text-sm">
                                                {single.ShipmentInfo ? 'No tracking events yet for this shipment.' : 'No tracking record found.'}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-[40vh] text-gray-400 text-sm">
                                        Enter a tracking number above to begin.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Tracking;